/* eslint-disable @typescript-eslint/camelcase */
import Breadcrumbs from 'components/Breadcrumbs';
import i18n from 'i18n';
import _ from 'lodash';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  editRoleRequest,
  getAllObjectsRequest,
  getObjectsRequest,
  getPermissionsRequest,
  getRoleInfoRequest,
  registerObjectsRequest,
  registerPermissionsRequest,
  removeObjectsRequest,
  toggleAddAllObjectsRequest,
} from 'modules/permission/actions';
import { RoleProps } from 'modules/permission/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import FormGroup from '../FormGroup';
import ContentClients from './Clients/Content';
import ContentCompanies from './Companies/Content';
import ContentEnvironments from './Environments/Content';
import ContentOperators from './Operators/Content';
import ContentPartners from './Partners/Content';

const CONFIG_TABS = [
  {
    label: 'COMPANIES',
    entity: 'company',
    count: 0,
    disabled: false,
  },
  {
    label: 'PARTNERS',
    entity: 'partner',
    count: 0,
    disabled: false,
  },
  {
    label: 'ENVIRONMENTS',
    entity: 'environment',
    count: 0,
    disabled: false,
  },
  {
    label: 'CLIENTS',
    entity: 'client',
    count: 0,
    disabled: false,
  },
  {
    label: 'OPERATORS',
    entity: 'operator',
    count: 0,
    disabled: false,
  },
];

export const mapAllSelected = {
  company: 'all_companies',
  partner: 'all_partners',
  environment: 'all_environments',
  client: 'all_clients',
};

export const initialQuery = {
  offset: 0,
  limit: 10,
  search: '',
};

interface Props {
  roleInfo?: RoleProps;
  isLoading: boolean;
  getRoleInfoRequest: (params: { role_uuid: string }) => Promise<void>;
  editRoleRequest: (params: { role_uuid: string; data: any }) => Promise<void>;
  getAllObjectsRequest: (params: { role_uuid: string }) => Promise<void>;
  getObjectsRequest: (props: {
    role_uuid: string;
    entity: string;
    query?: any;
  }) => Promise<void>;
  registerObjectsRequest: (props: {
    role_uuid: string;
    entity: string;
    objects: string[];
  }) => Promise<void>;
  removeObjectsRequest: (props: {
    role_uuid: string;
    entity: string;
    objects: string[];
  }) => Promise<void>;
  getPermissionsRequest: (props: {
    role_uuid: string;
    entity: string;
  }) => Promise<void>;
  registerPermissionsRequest: (props: {
    role_uuid: string;
    entity: string;
    permissions: string[];
  }) => Promise<void>;
  toggleAddAllObjectsRequest: (props: {
    role_uuid: string;
    entity: string;
    action: 'add' | 'remove';
  }) => Promise<void>;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
}

const EditGroups = ({
  roleInfo,
  isLoading,
  getRoleInfoRequest,
  editRoleRequest,
  getAllObjectsRequest,
  getObjectsRequest,
  registerObjectsRequest,
  removeObjectsRequest,
  getPermissionsRequest,
  registerPermissionsRequest,
  toggleAddAllObjectsRequest,
  modalOpen,
  modalClose,
}: Props) => {
  const { role_uuid } = useParams<{ role_uuid: string }>();
  const history = useHistory();

  const [currentTab, setCurrentTab] = React.useState(0);

  const TEMPLATE_PROPS = {
    title: '',
    subtitle: (
      <Breadcrumbs
        values={[
          {
            name: i18n.t('GROUPS_KEY'),
            link: '/groups',
          },
          {
            name: i18n.t('EDIT_KEY'),
          },
        ]}
      />
    ),
    tabs: {
      items: CONFIG_TABS,
      defaultValue: currentTab,
      onChange: setCurrentTab,
    },
  };

  const getObjects = _.debounce(
    (entity: string, query?: typeof initialQuery) => {
      getObjectsRequest({ role_uuid, entity, query: query || initialQuery });
    },
    500
  );

  const getPermissions = _.debounce((entity: string) => {
    getPermissionsRequest({ role_uuid, entity });
  }, 500);

  const handleCancel = () => history.push('/groups');

  const handleSaveObjects = (
    entity: string,
    objects: string[],
    allSelected?: boolean
  ) => {
    const condition =
      roleInfo && allSelected !== roleInfo[mapAllSelected[entity]];

    if (condition) {
      toggleAddAllObjectsRequest({
        role_uuid,
        entity,
        action: allSelected ? 'add' : 'remove',
      });
      return;
    }

    if (!allSelected && objects.length > 0) {
      registerObjectsRequest({
        objects,
        role_uuid,
        entity,
      });
    }
  };

  const handleRemoveObjects = (entity: string, objects: string[]) => {
    removeObjectsRequest({
      objects,
      role_uuid,
      entity,
    });
  };

  const handleSavePermissions = _.debounce(
    (entity: string, permissions: string[]) => {
      registerPermissionsRequest({
        permissions,
        role_uuid,
        entity,
      });
      getPermissions(CONFIG_TABS[currentTab].entity);
    },
    1000
  );

  const handleNextTab = () => setCurrentTab(currentTab + 1);
  const handlePrevTab = () => setCurrentTab(currentTab - 1);

  const editGroup = () => {
    modalOpen({
      title: i18n.t('EDIT_INFO_KEY'),
      content: (
        <FormGroup
          defaultValues={roleInfo}
          onSubmit={({ name, description, active, restricted_operators }) =>
            editRoleRequest({
              role_uuid,
              data: { name, description, active, restricted_operators },
            })
          }
          onCancel={modalClose}
          isLoading={isLoading}
        />
      ),
      maxWidth: 'sm',
    });
  };

  React.useEffect(() => {
    getPermissions(CONFIG_TABS[currentTab].entity);
    getObjects(CONFIG_TABS[currentTab].entity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  React.useEffect(() => {
    getRoleInfoRequest({ role_uuid });
    getAllObjectsRequest({ role_uuid });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <React.Fragment>
      <ContentCompanies
        id={role_uuid}
        hidden={currentTab !== 0}
        template={{
          ...TEMPLATE_PROPS,
          title: roleInfo?.name || '',
          slug: 'COMPANIES',
        }}
        onLoad={getObjects}
        onCancel={handleCancel}
        onRemove={handleRemoveObjects}
        onNextStep={handleNextTab}
        updateObjects={handleSaveObjects}
        updatePermissions={handleSavePermissions}
        editGroup={editGroup}
      />
      <ContentPartners
        id={role_uuid}
        hidden={currentTab !== 1}
        template={{
          ...TEMPLATE_PROPS,
          title: roleInfo?.name || '',
          slug: 'PARTNERS',
        }}
        onLoad={getObjects}
        onCancel={handleCancel}
        onRemove={handleRemoveObjects}
        onPrevStep={handlePrevTab}
        onNextStep={handleNextTab}
        updateObjects={handleSaveObjects}
        updatePermissions={handleSavePermissions}
        editGroup={editGroup}
      />
      <ContentEnvironments
        id={role_uuid}
        hidden={currentTab !== 2}
        template={{
          ...TEMPLATE_PROPS,
          title: roleInfo?.name || '',
          slug: 'ENVIRONMENTS',
        }}
        onLoad={getObjects}
        onCancel={handleCancel}
        onRemove={handleRemoveObjects}
        onPrevStep={handlePrevTab}
        onNextStep={handleNextTab}
        updateObjects={handleSaveObjects}
        updatePermissions={handleSavePermissions}
        editGroup={editGroup}
      />
      <ContentClients
        id={role_uuid}
        hidden={currentTab !== 3}
        template={{
          ...TEMPLATE_PROPS,
          title: roleInfo?.name || '',
          slug: 'CLIENTS',
        }}
        onLoad={getObjects}
        onCancel={handleCancel}
        onRemove={handleRemoveObjects}
        onPrevStep={handlePrevTab}
        onNextStep={handleNextTab}
        updateObjects={handleSaveObjects}
        updatePermissions={handleSavePermissions}
        editGroup={editGroup}
      />
      <ContentOperators
        id={role_uuid}
        hidden={currentTab !== 4}
        template={{
          ...TEMPLATE_PROPS,
          title: roleInfo?.name || '',
        }}
        onLoad={getObjects}
        onCancel={handleCancel}
        onRemove={handleRemoveObjects}
        onPrevStep={handlePrevTab}
        updateObjects={handleSaveObjects}
        updatePermissions={handleSavePermissions}
        editGroup={editGroup}
      />
    </React.Fragment>
  );
};

const mapStateToProps = ({
  permission: { roleInfo, isLoading },
}: ApplicationState) => ({
  roleInfo,
  isLoading,
});

export default connect(mapStateToProps, {
  getRoleInfoRequest,
  editRoleRequest,
  getAllObjectsRequest,
  getObjectsRequest,
  registerObjectsRequest,
  removeObjectsRequest,
  getPermissionsRequest,
  registerPermissionsRequest,
  toggleAddAllObjectsRequest,
  modalOpen,
  modalClose,
})(EditGroups);
